import { captureException } from '@sentry/react';
import { ParselyTopArticle, ParselyRelatedArticle } from 'rest/parsely/types';
import Cookies from 'js-cookie';

/**
 * Slightly better than "any"
 */
type GenericObject = { [key: string]: any };

const IDENTITY_COOKIE_NAME = 'zapidentity';

export async function trackParselyPageView({
  location,
}: {
  location: Location;
}) {
  /**
   * Construct the full canonical URL
   *  Include protocol (should be HTTPS), domain, and pathname.
   *  Exclude query parameters
   *
   * From https://developers.google.com/search/docs/advanced/crawling/consolidate-duplicate-urls?hl=en&visit_id=637479806440806338-1859371089&rd=1
   *  Use absolute paths rather than relative paths with the rel="canonical" link element.
   *    Use this structure: https://www.example.com/dresses/green/greendresss.html
   *    Not this structure: /dresses/green/greendress.html
   */
  const url = new URL(location.href);
  url.search = '';
  const fullUrl = url.toString();

  const cookieUUID = Cookies.get(IDENTITY_COOKIE_NAME);

  // Get identity from cookie, this _should_ be set but it MAY not be set
  if (!cookieUUID) {
    // If this happens a lot we'll consider how to solve this further
    // This might be very rare. Let's send it to Sentry and see how rare it is.
    const error = new Error(`${IDENTITY_COOKIE_NAME} not set at ${fullUrl}`);
    captureException(error);
    return;
  }

  if (!cookieUUID.length) {
    return;
  }

  /**
   * https://www.parse.ly/help/api/recommendations#get-profile
   */
  return parselyApiRequest('/profile', {
    /**
     * UUID for user profile being trained.
     */
    uuid: cookieUUID,
    /**
     * Canonical URL for page being trained against profile.
     */
    url: fullUrl,
  });
}

export async function getParselyTopArticlesSlugs() {
  const {
    data: articles,
  }: { data: ParselyTopArticle[] } = await parselyApiRequest('/top/posts', {});

  return articles.map((article) => extractSlugFromParselyURL(article.url));
}

export async function getParselyRelatedArticlesSlugs({
  limit,
  section,
  tag,
  analyticsCookiesAccepted,
}: {
  limit: number;
  section?: string;
  tag?: string;
  analyticsCookiesAccepted: boolean;
}) {
  const cookieUUID = analyticsCookiesAccepted
    ? Cookies.get(IDENTITY_COOKIE_NAME)
    : undefined;

  const {
    data: articles,
  }: { data: ParselyRelatedArticle[] } = await parselyApiRequest('/related', {
    limit,
    uuid: cookieUUID,
    ...(section && { section }),
    ...(tag && { tag }),
  });
  return articles
    .filter((articles) => articles.url.includes('https://zapier.com/blog/'))
    .map((article) => extractSlugFromParselyURL(article.url));
}

/**
 * Client-side Parsely request for JSON GET requests containing apikey
 *
 * https://www.parse.ly/help/api/endpoint
 */
async function parselyApiRequest(
  endpoint: string,
  params: GenericObject | undefined,
) {
  const PARSELY_API_BASE = 'https://api.parsely.com/v2';
  const PARSELY_API_KEY = process.env.NEXT_PUBLIC_PARSELY_API_KEY;

  if (!PARSELY_API_KEY) {
    throw new Error('NEXT_PUBLIC_PARSELY_API_KEY not set');
  }

  const url = PARSELY_API_BASE + endpoint;
  return await jsonGetRequest(url, {
    ...params,
    /**
     * apikey is constant and required for all requests
     * See https://www.parse.ly/help/api/endpoint#request
     */
    apikey: PARSELY_API_KEY,
  });
}

/**
 * JSON GET fetch request
 * https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 */
async function jsonGetRequest(url: string, params: GenericObject | undefined) {
  const urlInstance = new URL(url);

  if (params) {
    urlInstance.search = new URLSearchParams(params).toString();
  }

  const response = await fetch(urlInstance.toString(), {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status#successful_responses
  if (!response.ok) {
    throw new Error(
      `fetch request to URL ${urlInstance} failed with code ${response.status}`,
    );
  }

  return await response.json();
}

function extractSlugFromParselyURL(articleSlug: string) {
  const blogUrl = new URL(articleSlug);
  return blogUrl.pathname.split('/blog/')[1].split('/')[0];
}
